import classNames from 'classnames';
import hasProperty from 'lodash/has';

import { Model } from '@adobe/aem-spa-page-model-manager';

import { GridContainer } from '../../grid-container/grid-container';
import { GridProps } from '../../grid/grid';
import { getComponentFromProps, getPropsFromModel } from '../../../utils/use-child-components';

import { ColumnContentTypes } from './column-content-container.config';

type ColumnContentGridProps = {
  items: Record<string, Model>;
  itemsOrder?: string[];
  cqPath: string;
  gridContainerClass?: string;
  columnContentType?: ColumnContentTypes;
  hasGradientBackground?: boolean;
};

const getGridSizes = (columnContentType?: ColumnContentTypes) => {
  if (columnContentType === ColumnContentTypes.TWO_COLUMNS) {
    return { smallDesktop: 6, desktop: 6 };
  }

  if (columnContentType === ColumnContentTypes.THREE_COLUMNS) {
    return { smallDesktop: 4, desktop: 4 };
  }

  return { smallDesktop: 3, desktop: 3 };
};

export const getGridProps = (_: number, columnContentType?: ColumnContentTypes): GridProps => ({
  mobile: 12,
  tablet: 6,
  ...getGridSizes(columnContentType),
  className: 'snowflake-column-content-container-content-block-grid',
});

export const ColumnContentGrid = ({
  items,
  itemsOrder,
  cqPath,
  columnContentType,
  gridContainerClass,
  hasGradientBackground,
}: ColumnContentGridProps) => {
  const elementPropList = (itemsOrder || []).map((key) => {
    const item = items[key];
    const itemProps = getPropsFromModel(item, cqPath, key);
    const Item = getComponentFromProps(itemProps);

    return { key, elementProps: { ...itemProps, Item } };
  });

  const getItemGridProps = (index: number) => getGridProps(index, columnContentType);

  const isCardsOnly = Object?.values(items || {})?.every((item) => !!(item as Record<string, Model>)?.eyebrow);

  return (
    <GridContainer
      gridContainerClass={classNames({ 'snowflake-column-content-grid': !isCardsOnly }, gridContainerClass)}
      elementPropList={elementPropList}
      getGridProps={getItemGridProps}
      renderItem={({ elementProps: { Item, ...itemProps } }, addSameHeightRef, wrapperMinHeights) =>
        hasProperty(itemProps, 'eyebrow') ? (
          <Item
            titleRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'title');
            }}
            eyebrowRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'eyebrow');
            }}
            buttonDivRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'buttonDiv');
            }}
            titleWrapperMinHeight={wrapperMinHeights.title}
            eyebrowWrapperMinHeight={wrapperMinHeights.eyebrow}
            buttonDivMinHeight={wrapperMinHeights.buttonDiv}
            {...itemProps}
          />
        ) : (
          <Item
            appliedCssClassNames={classNames({ 'snowflake-content-block-dark-bg': hasGradientBackground })}
            titleRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'title');
            }}
            textRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'text');
            }}
            ctaRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'cta');
            }}
            titleMinHeight={wrapperMinHeights.title}
            textMinHeight={wrapperMinHeights.text}
            ctaMinHeight={wrapperMinHeights.cta}
            logo={itemProps?.logo}
            icon={itemProps?.icon}
            cta={itemProps?.cta}
            brandedImagery={itemProps?.brandedImagery}
            title={itemProps?.title}
            {...itemProps}
          />
        )
      }
    />
  );
};
